import { useState, useEffect, useCallback, useMemo } from 'react'
import {
  Box, Title, Text, Button, Paper, Group, Stack, Modal, TextInput, Textarea,
  Select, Badge, Loader, ActionIcon, ScrollArea, Divider, Pagination, Table,
  Switch, NumberInput, Tooltip,
} from '@mantine/core'
import { useDebouncedValue } from '@mantine/hooks'
import { notifications } from '@mantine/notifications'
import {
  Search, Edit2, Trash2, CheckCircle, Eye, X, Plus, ChevronDown,
  FileQuestion, Zap, BookOpen, Hash, Save, AlertCircle,
} from 'lucide-react'
import { contentApi, Portion, Domain, Mastery } from '../../api/content'
import {
  adminQuestionsApi, QuestionSet, QuestionAnswer, AnswerChoice,
  QuestionSetUpdatePayload, QuestionAnswerInput, ChoiceInput,
} from '../../api/questions'
import { QuestionSetComponent } from '../../components/questions/QuestionSetComponent'

const PAGE_SIZE = 20
const LABELS = ['A', 'B', 'C', 'D']

export function QuestionManager() {
  // --- Filter state ---
  const [searchText, setSearchText] = useState('')
  const [debouncedSearch] = useDebouncedValue(searchText, 400)
  const [portions, setPortions] = useState<Portion[]>([])
  const [domains, setDomains] = useState<Domain[]>([])
  const [masteries, setMasteries] = useState<Mastery[]>([])
  const [sources, setSources] = useState<string[]>([])
  const [selectedPortionId, setSelectedPortionId] = useState<string | null>(null)
  const [selectedDomainId, setSelectedDomainId] = useState<string | null>(null)
  const [selectedMasteryId, setSelectedMasteryId] = useState<string | null>(null)
  const [selectedSource, setSelectedSource] = useState<string | null>(null)
  const [questionSetIdStr, setQuestionSetIdStr] = useState('')
  const [debouncedQuestionSetIdStr] = useDebouncedValue(questionSetIdStr, 400)
  const questionSetIdFilter = useMemo(() => {
    const t = debouncedQuestionSetIdStr.trim()
    if (!t) return undefined
    const n = Number(t)
    return Number.isInteger(n) && n >= 1 ? n : undefined
  }, [debouncedQuestionSetIdStr])

  // --- List state ---
  const [questions, setQuestions] = useState<QuestionSet[]>([])
  const [total, setTotal] = useState(0)
  const [page, setPage] = useState(1)
  const [loading, setLoading] = useState(false)
  const [highlightedQuestionSetId, setHighlightedQuestionSetId] = useState<number | null>(null)

  // --- Modal state ---
  const [modalOpen, setModalOpen] = useState(false)
  const [selectedQ, setSelectedQ] = useState<QuestionSet | null>(null)
  const [editMode, setEditMode] = useState(false)
  const [saving, setSaving] = useState(false)

  // --- Edit form state ---
  const [editData, setEditData] = useState<QuestionSetUpdatePayload>({})
  const [editQAs, setEditQAs] = useState<QuestionAnswerInput[]>([])

  // --- Load portions on mount ---
  useEffect(() => {
    contentApi.getPortions().then((res) => setPortions(res.data))
    adminQuestionsApi.listSources().then((res) => setSources(res.data)).catch(() => setSources([]))
  }, [])

  // --- Load domains when portion changes ---
  useEffect(() => {
    setDomains([])
    setMasteries([])
    setSelectedDomainId(null)
    setSelectedMasteryId(null)
    if (selectedPortionId) {
      contentApi.getDomains(Number(selectedPortionId)).then((res) => setDomains(res.data))
    }
  }, [selectedPortionId])

  // --- Load masteries when domain changes ---
  useEffect(() => {
    setMasteries([])
    setSelectedMasteryId(null)
    if (selectedDomainId) {
      contentApi.getMasteries(Number(selectedDomainId)).then((res) => setMasteries(res.data))
    }
  }, [selectedDomainId])

  // --- Fetch questions ---
  const fetchQuestions = useCallback(async () => {
    setLoading(true)
    try {
      const res = await adminQuestionsApi.list({
        search: debouncedSearch || undefined,
        portion_id: selectedPortionId ? Number(selectedPortionId) : undefined,
        domain_id: selectedDomainId ? Number(selectedDomainId) : undefined,
        mastery_id: selectedMasteryId ? Number(selectedMasteryId) : undefined,
        source: selectedSource || undefined,
        question_set_id: questionSetIdFilter,
        skip: (page - 1) * PAGE_SIZE,
        limit: PAGE_SIZE,
      })
      setQuestions(res.data.items)
      setTotal(res.data.total)
    } catch {
      setQuestions([])
      setTotal(0)
    } finally {
      setLoading(false)
    }
  }, [debouncedSearch, selectedPortionId, selectedDomainId, selectedMasteryId, selectedSource, questionSetIdFilter, page])

  useEffect(() => { fetchQuestions() }, [fetchQuestions])

  // Reset page when filters change
  useEffect(() => { setPage(1) }, [debouncedSearch, selectedPortionId, selectedDomainId, selectedMasteryId, selectedSource, debouncedQuestionSetIdStr])

  // --- Open detail modal ---
  const openDetail = (q: QuestionSet) => {
    setHighlightedQuestionSetId(q.id)
    setSelectedQ(q)
    setEditMode(false)
    setModalOpen(true)
  }

  // --- Enter edit mode ---
  const enterEdit = () => {
    if (!selectedQ) return
    setEditData({
      source: selectedQ.source,
      module: selectedQ.module,
      sequence_in_source: selectedQ.sequence_in_source,
      question_text: selectedQ.question_text,
      difficulty_level: selectedQ.difficulty_level,
      explanation: selectedQ.explanation,
      verified_flag: selectedQ.verified_flag,
    })
    setEditQAs(selectedQ.question_answers.map(qaToInput))
    setEditMode(true)
  }

  const qaToInput = (qa: QuestionAnswer): QuestionAnswerInput => ({
    question: qa.question,
    question_image: qa.question_image,
    display_order: qa.display_order,
    explanation: qa.explanation,
    question_type: qa.question_type || 'multiple_choice',
    correct_answer_text: qa.correct_answer_text,
    answer_tolerance: qa.answer_tolerance,
    choices: qa.choices.map((c) => ({
      choice_label: c.choice_label,
      choice_text: c.choice_text,
      is_correct: c.is_correct,
      explanation: c.explanation || '',
      display_order: c.display_order,
    })),
  })

  // --- Save edits ---
  const handleSave = async () => {
    if (!selectedQ) return
    setSaving(true)
    try {
      await adminQuestionsApi.update(selectedQ.id, {
        ...editData,
        question_answers: editQAs,
      })
      notifications.show({ title: 'Saved', message: 'Question updated successfully.', color: 'green' })
      setEditMode(false)
      setModalOpen(false)
      fetchQuestions()
    } catch (err: any) {
      notifications.show({ title: 'Error', message: err.response?.data?.detail || 'Failed to save', color: 'red' })
    } finally {
      setSaving(false)
    }
  }

  // --- Delete ---
  const handleDelete = async (q: QuestionSet) => {
    if (!confirm(`Delete question set #${q.id}? This cannot be undone.`)) return
    try {
      await adminQuestionsApi.delete(q.id)
      if (highlightedQuestionSetId === q.id) setHighlightedQuestionSetId(null)
      notifications.show({ title: 'Deleted', message: 'Question deleted.', color: 'orange' })
      fetchQuestions()
    } catch (err: any) {
      notifications.show({ title: 'Error', message: err.response?.data?.detail || 'Failed', color: 'red' })
    }
  }

  // --- Verify ---
  const handleVerify = async (q: QuestionSet) => {
    try {
      await adminQuestionsApi.verify(q.id)
      notifications.show({ title: 'Verified', message: 'Question marked as verified.', color: 'green' })
      fetchQuestions()
    } catch (err: any) {
      notifications.show({ title: 'Error', message: err.response?.data?.detail || 'Failed', color: 'red' })
    }
  }

  // --- Edit helpers ---
  const updateQA = (idx: number, field: string, value: any) => {
    setEditQAs((prev) => prev.map((qa, i) => i === idx ? { ...qa, [field]: value } : qa))
  }

  const updateChoice = (qaIdx: number, cIdx: number, field: string, value: any) => {
    setEditQAs((prev) => prev.map((qa, i) => {
      if (i !== qaIdx) return qa
      const newChoices = qa.choices.map((c, j) => {
        if (j !== cIdx) {
          // If setting is_correct, clear other choices
          if (field === 'is_correct' && value === true) return { ...c, is_correct: false }
          return c
        }
        return { ...c, [field]: value }
      })
      return { ...qa, choices: newChoices }
    }))
  }

  const addChoice = (qaIdx: number) => {
    setEditQAs((prev) => prev.map((qa, i) => {
      if (i !== qaIdx) return qa
      const usedLabels = new Set(qa.choices.map((c) => c.choice_label))
      let nextLabel = ''
      for (let code = 65; code < 91; code++) {
        const letter = String.fromCharCode(code)
        if (!usedLabels.has(letter)) { nextLabel = letter; break }
      }
      return {
        ...qa,
        choices: [...qa.choices, { choice_label: nextLabel, choice_text: '', is_correct: false, explanation: '', display_order: qa.choices.length }],
      }
    }))
  }

  const removeChoice = (qaIdx: number, cIdx: number) => {
    setEditQAs((prev) => prev.map((qa, i) => {
      if (i !== qaIdx) return qa
      const newChoices = qa.choices.filter((_, j) => j !== cIdx).map((c, j) => ({
        ...c,
        choice_label: LABELS[j] || String.fromCharCode(65 + j),
        display_order: j,
      }))
      return { ...qa, choices: newChoices }
    }))
  }

  const addQuestionAnswer = () => {
    setEditQAs((prev) => [...prev, {
      question: '',
      question_image: null,
      display_order: prev.length,
      explanation: null,
      question_type: 'multiple_choice',
      correct_answer_text: null,
      answer_tolerance: null,
      choices: LABELS.map((l, i) => ({ choice_label: l, choice_text: '', is_correct: i === 0, explanation: '', display_order: i })),
    }])
  }

  const removeQuestionAnswer = (idx: number) => {
    setEditQAs((prev) => prev.filter((_, i) => i !== idx).map((qa, i) => ({ ...qa, display_order: i })))
  }

  // --- Get first question text for table preview ---
  const getPreview = (q: QuestionSet): string => {
    // Prefer the first question_answer text, fall back to question_text (passage)
    const firstQA = q.question_answers[0]
    const text = firstQA?.question || q.question_text || '(no text)'
    return text.length > 100 ? text.slice(0, 100) + '...' : text
  }

  const totalPages = Math.ceil(total / PAGE_SIZE)

  return (
    <Box style={{ display: 'flex', flexDirection: 'column', height: 'calc(100vh - 64px)', overflow: 'hidden' }}>
      {/* Header */}
      <Box style={{ padding: '16px 24px', borderBottom: '1px solid var(--sat-border-soft)', background: 'var(--sat-surface)' }}>
        <Group justify="space-between" mb={12}>
          <Group gap={10}>
            <FileQuestion size={22} color="var(--sat-red)" />
            <Title order={3} c="var(--sat-text)">Question Set Manager</Title>
            <Badge size="lg" color="gray" variant="light">{total} total</Badge>
          </Group>
        </Group>

        {/* Filters */}
        <Group gap={10} wrap="wrap">
          <TextInput
            placeholder="Question set ID"
            leftSection={<Hash size={14} />}
            value={questionSetIdStr}
            onChange={(e) => setQuestionSetIdStr(e.currentTarget.value.replace(/\D/g, ''))}
            size="sm"
            style={{ width: 160 }}
          />
          <TextInput
            placeholder="Search question text..."
            leftSection={<Search size={14} />}
            value={searchText}
            onChange={(e) => setSearchText(e.currentTarget.value)}
            style={{ flex: 1, minWidth: 200 }}
            size="sm"
          />
          <Select
            placeholder="Portion"
            data={portions.map((p) => ({ value: String(p.id), label: p.name }))}
            value={selectedPortionId}
            onChange={setSelectedPortionId}
            clearable
            size="sm"
            style={{ width: 160 }}
          />
          <Select
            placeholder="Domain"
            data={domains.map((d) => ({ value: String(d.id), label: d.name }))}
            value={selectedDomainId}
            onChange={setSelectedDomainId}
            clearable
            disabled={!selectedPortionId}
            size="sm"
            style={{ width: 220 }}
          />
          <Select
            placeholder="Mastery"
            data={masteries.map((m) => ({ value: String(m.id), label: m.name }))}
            value={selectedMasteryId}
            onChange={setSelectedMasteryId}
            clearable
            disabled={!selectedDomainId}
            size="sm"
            style={{ width: 220 }}
          />
          <Select
            placeholder="Source"
            data={sources.map((source) => ({ value: source, label: source }))}
            value={selectedSource}
            onChange={setSelectedSource}
            clearable
            searchable
            size="sm"
            style={{ width: 180 }}
          />
        </Group>
      </Box>

      {/* Table */}
      <Box style={{ flex: 1, overflow: 'auto' }}>
        {loading ? (
          <Box style={{ display: 'flex', justifyContent: 'center', paddingTop: 80 }}>
            <Loader color="eveningSea" />
          </Box>
        ) : questions.length === 0 ? (
          <Box style={{ textAlign: 'center', paddingTop: 80 }}>
            <BookOpen size={48} color="var(--sat-blush)" style={{ margin: '0 auto 12px' }} />
            <Text c="var(--sat-text-faint)">No questions found. Adjust filters.</Text>
          </Box>
        ) : (
          <Table highlightOnHover verticalSpacing="sm" style={{ tableLayout: 'fixed' }}>
            <Table.Thead style={{ position: 'sticky', top: 0, background: 'var(--sat-surface)', zIndex: 1 }}>
              <Table.Tr>
                <Table.Th style={{ width: 60 }}>ID</Table.Th>
                <Table.Th>Question Preview</Table.Th>
                <Table.Th style={{ width: 160 }}>Source</Table.Th>
                <Table.Th style={{ width: 90 }}>Module</Table.Th>
                <Table.Th style={{ width: 90 }}>Seq</Table.Th>
                <Table.Th style={{ width: 60 }}>QAs</Table.Th>
                <Table.Th style={{ width: 50 }}>Diff</Table.Th>
                <Table.Th style={{ width: 110 }}>Status</Table.Th>
                <Table.Th style={{ width: 100 }}>Actions</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {questions.map((q) => (
                <Table.Tr
                  key={q.id}
                  style={{
                    cursor: 'pointer',
                    ...(highlightedQuestionSetId === q.id
                      ? { backgroundColor: 'var(--mantine-color-orange-9)' }
                      : {}),
                  }}
                  onClick={() => openDetail(q)}
                >
                  <Table.Td>
                    <Text size="xs" c="var(--sat-text-faint)">#{q.id}</Text>
                  </Table.Td>
                  <Table.Td>
                    <Text size="sm" c="var(--sat-text)" lineClamp={2} style={{ lineHeight: 1.4 }}>
                      {getPreview(q)}
                    </Text>
                  </Table.Td>
                  <Table.Td>
                    <Badge size="xs" color="gray" variant="light">
                      {q.source || 'No source'}
                    </Badge>
                  </Table.Td>
                  <Table.Td>
                    <Text size="xs" c="var(--sat-text-faint)">
                      {q.module || '—'}
                    </Text>
                  </Table.Td>
                  <Table.Td>
                    <Text size="xs" c="var(--sat-text-faint)">
                      {q.sequence_in_source ?? '—'}
                    </Text>
                  </Table.Td>
                  <Table.Td>
                    <Badge size="xs" color="blue" variant="light">{q.question_answers.length}</Badge>
                  </Table.Td>
                  <Table.Td>
                    <Badge size="xs" color="gray">D{q.difficulty_level}</Badge>
                  </Table.Td>
                  <Table.Td>
                    <Group gap={4}>
                      {q.ai_flag && <Badge size="xs" color="grape" leftSection={<Zap size={8} />}>AI</Badge>}
                      {q.verified_flag
                        ? <Badge size="xs" color="green">Verified</Badge>
                        : <Badge size="xs" color="yellow">Unverified</Badge>}
                    </Group>
                  </Table.Td>
                  <Table.Td>
                    <Group gap={4} onClick={(e) => e.stopPropagation()}>
                      <Tooltip label="View">
                        <ActionIcon size="sm" variant="subtle" color="blue" onClick={() => openDetail(q)}>
                          <Eye size={14} />
                        </ActionIcon>
                      </Tooltip>
                      {!q.verified_flag && (
                        <Tooltip label="Verify">
                          <ActionIcon size="sm" variant="subtle" color="green" onClick={() => handleVerify(q)}>
                            <CheckCircle size={14} />
                          </ActionIcon>
                        </Tooltip>
                      )}
                      <Tooltip label="Delete">
                        <ActionIcon size="sm" variant="subtle" color="red" onClick={() => handleDelete(q)}>
                          <Trash2 size={14} />
                        </ActionIcon>
                      </Tooltip>
                    </Group>
                  </Table.Td>
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>
        )}
      </Box>

      {/* Pagination */}
      {totalPages > 1 && (
        <Box style={{ padding: '12px 24px', borderTop: '1px solid var(--sat-border-soft)', background: 'var(--sat-surface)' }}>
          <Group justify="center">
            <Pagination
              total={totalPages}
              value={page}
              onChange={setPage}
              color="eveningSea"
              size="sm"
            />
            <Text size="xs" c="var(--sat-text-faint)">
              {(page - 1) * PAGE_SIZE + 1}–{Math.min(page * PAGE_SIZE, total)} of {total}
            </Text>
          </Group>
        </Box>
      )}

      {/* Detail / Edit Modal */}
      <Modal
        opened={modalOpen}
        onClose={() => { setModalOpen(false); setEditMode(false) }}
        title={
          <Group gap={10}>
            <Text fw={700} c="var(--sat-text)" size="lg">
              {editMode ? 'Edit' : 'Question Set'} #{selectedQ?.id}
            </Text>
            {selectedQ && !editMode && (
              <Group gap={4}>
                {selectedQ.source && <Badge size="xs" color="gray" variant="light">{selectedQ.source}</Badge>}
                {selectedQ.module && <Badge size="xs" color="blue" variant="light">Module {selectedQ.module}</Badge>}
                {selectedQ.sequence_in_source != null && (
                  <Badge size="xs" color="cyan" variant="light">#{selectedQ.sequence_in_source}</Badge>
                )}
                {selectedQ.ai_flag && <Badge size="xs" color="grape">AI</Badge>}
                {selectedQ.verified_flag
                  ? <Badge size="xs" color="green">Verified</Badge>
                  : <Badge size="xs" color="yellow">Unverified</Badge>}
                <Badge size="xs" color="gray">Difficulty {selectedQ.difficulty_level}</Badge>
              </Group>
            )}
          </Group>
        }
        size="xl"
        radius="md"
        scrollAreaComponent={ScrollArea.Autosize}
        styles={{ body: { maxHeight: 'calc(100vh - 160px)' } }}
      >
        {selectedQ && !editMode && <QuestionSetComponent initialValue={selectedQ} onEdit={enterEdit} />}
        {selectedQ && editMode && (
          <EditView
            editData={editData}
            setEditData={setEditData}
            editQAs={editQAs}
            updateQA={updateQA}
            updateChoice={updateChoice}
            addChoice={addChoice}
            removeChoice={removeChoice}
            addQuestionAnswer={addQuestionAnswer}
            removeQuestionAnswer={removeQuestionAnswer}
            onSave={handleSave}
            onCancel={() => setEditMode(false)}
            saving={saving}
          />
        )}
      </Modal>
    </Box>
  )
}

/* ============================== Edit View ============================== */

interface EditViewProps {
  editData: QuestionSetUpdatePayload
  setEditData: React.Dispatch<React.SetStateAction<QuestionSetUpdatePayload>>
  editQAs: QuestionAnswerInput[]
  updateQA: (idx: number, field: string, value: any) => void
  updateChoice: (qaIdx: number, cIdx: number, field: string, value: any) => void
  addChoice: (qaIdx: number) => void
  removeChoice: (qaIdx: number, cIdx: number) => void
  addQuestionAnswer: () => void
  removeQuestionAnswer: (idx: number) => void
  onSave: () => void
  onCancel: () => void
  saving: boolean
}

function EditView({
  editData, setEditData, editQAs, updateQA, updateChoice,
  addChoice, removeChoice, addQuestionAnswer, removeQuestionAnswer,
  onSave, onCancel, saving,
}: EditViewProps) {
  return (
    <Stack gap={16}>
      {/* Question Set fields */}
      <Paper p="md" radius="md" style={{ background: 'var(--sat-surface-alt)', border: '1px solid var(--sat-border-soft)' }}>
        <Text size="xs" fw={600} c="var(--sat-text-faint)" mb={8}>QUESTION SET</Text>
        <Stack gap={10}>
          <Group grow align="flex-end">
            <TextInput
              label="Source"
              placeholder="e.g. bluebook 4"
              value={editData.source || ''}
              onChange={(e) => {
                const v = e.currentTarget.value
                setEditData((prev) => ({ ...prev, source: v || null }))
              }}
            />
            <TextInput
              label="Module"
              placeholder="e.g. M1"
              value={editData.module || ''}
              onChange={(e) => {
                const v = e.currentTarget.value
                setEditData((prev) => ({ ...prev, module: v || null }))
              }}
            />
            <NumberInput
              label="Sequence In Source"
              placeholder="e.g. 12"
              value={editData.sequence_in_source ?? ''}
              onChange={(v) => setEditData((prev) => ({ ...prev, sequence_in_source: typeof v === 'number' ? v : null }))}
              min={1}
              allowDecimal={false}
            />
          </Group>
          <Textarea
            label="Passage / Context"
            placeholder="Passage text (supports LaTeX: $x^2$)"
            rows={4}
            value={editData.question_text || ''}
            onChange={(e) => { const v = e.currentTarget.value; setEditData((prev) => ({ ...prev, question_text: v })) }}
          />
          <Group gap={12}>
            <Select
              label="Difficulty"
              data={[
                { value: '1', label: '1 — Very Easy' },
                { value: '2', label: '2 — Easy' },
                { value: '3', label: '3 — Medium' },
                { value: '4', label: '4 — Hard' },
                { value: '5', label: '5 — Very Hard' },
              ]}
              value={String(editData.difficulty_level ?? 3)}
              onChange={(v) => setEditData((prev) => ({ ...prev, difficulty_level: Number(v) }))}
              style={{ width: 180 }}
            />
            <Switch
              label="Verified"
              checked={editData.verified_flag ?? false}
              onChange={(e) => { const v = e.currentTarget.checked; setEditData((prev) => ({ ...prev, verified_flag: v })) }}
              color="green"
              mt={24}
            />
          </Group>
          <Textarea
            label="Set Explanation"
            placeholder="Overall explanation (optional)"
            rows={2}
            value={editData.explanation || ''}
            onChange={(e) => { const v = e.currentTarget.value; setEditData((prev) => ({ ...prev, explanation: v })) }}
          />
        </Stack>
      </Paper>

      <Divider label={`Questions (${editQAs.length})`} labelPosition="left" />

      {/* Question Answers */}
      {editQAs.map((qa, qaIdx) => (
        <QuestionAnswerEdit
          key={qaIdx}
          qa={qa}
          index={qaIdx}
          updateQA={updateQA}
          updateChoice={updateChoice}
          addChoice={addChoice}
          removeChoice={removeChoice}
          onRemove={() => removeQuestionAnswer(qaIdx)}
          canRemove={editQAs.length > 1}
        />
      ))}

      <Button
        leftSection={<Plus size={14} />}
        variant="light"
        color="blue"
        size="xs"
        onClick={addQuestionAnswer}
      >
        Add Question
      </Button>

      {/* Save / Cancel */}
      <Divider />
      <Group justify="flex-end">
        <Button variant="subtle" color="gray" onClick={onCancel}>Cancel</Button>
        <Button leftSection={<Save size={14} />} color="grapefruit" loading={saving} onClick={onSave}>
          Save Changes
        </Button>
      </Group>
    </Stack>
  )
}

function QuestionAnswerEdit({
  qa, index, updateQA, updateChoice, addChoice, removeChoice, onRemove, canRemove,
}: {
  qa: QuestionAnswerInput
  index: number
  updateQA: (idx: number, field: string, value: any) => void
  updateChoice: (qaIdx: number, cIdx: number, field: string, value: any) => void
  addChoice: (qaIdx: number) => void
  removeChoice: (qaIdx: number, cIdx: number) => void
  onRemove: () => void
  canRemove: boolean
}) {
  return (
    <Paper p="md" radius="md" style={{ border: '1px solid var(--sat-border-soft)' }}>
      <Group justify="space-between" mb={10}>
        <Group gap={8}>
          <Badge size="sm" color="blue" variant="light" circle>{index + 1}</Badge>
          <Select
            size="xs"
            data={[
              { value: 'multiple_choice', label: 'Multiple Choice' },
              { value: 'spr', label: 'Student-Produced Response' },
            ]}
            value={qa.question_type}
            onChange={(v) => updateQA(index, 'question_type', v || 'multiple_choice')}
            style={{ width: 200 }}
          />
        </Group>
        {canRemove && (
          <ActionIcon size="sm" variant="subtle" color="red" onClick={onRemove}>
            <Trash2 size={14} />
          </ActionIcon>
        )}
      </Group>

      <Stack gap={10}>
        <Textarea
          label="Question Text"
          placeholder="Enter question (supports LaTeX: $x^2$)"
          rows={3}
          value={qa.question}
          onChange={(e) => updateQA(index, 'question', e.currentTarget.value)}
          required
        />

        {qa.question_type === 'spr' ? (
          <Group gap={12}>
            <TextInput
              label="Correct Answer"
              placeholder="e.g. 3.5, 7, 1/2"
              value={qa.correct_answer_text || ''}
              onChange={(e) => updateQA(index, 'correct_answer_text', e.currentTarget.value || '')}
              style={{ flex: 1 }}
            />
            <NumberInput
              label="Tolerance (±)"
              placeholder="0"
              value={qa.answer_tolerance ?? ''}
              onChange={(v) => updateQA(index, 'answer_tolerance', v || null)}
              style={{ width: 120 }}
              min={0}
              step={0.1}
            />
          </Group>
        ) : (
          <>
            <Text size="xs" fw={600} c="var(--sat-text-faint)">CHOICES</Text>
            <Stack gap={8}>
              {qa.choices.map((c, cIdx) => (
                <Paper key={cIdx} p={8} radius="sm" style={{
                  border: c.is_correct ? '1px solid var(--sat-green-border-soft)' : '1px solid var(--sat-border-soft)',
                  background: c.is_correct ? 'var(--sat-green-soft)' : 'transparent',
                }}>
                  <Group gap={8} align="flex-start">
                    <Box mt={4}>
                      <Tooltip label="Mark as correct">
                        <ActionIcon
                          size="sm"
                          variant={c.is_correct ? 'filled' : 'subtle'}
                          color="green"
                          onClick={() => updateChoice(index, cIdx, 'is_correct', true)}
                        >
                          <CheckCircle size={14} />
                        </ActionIcon>
                      </Tooltip>
                    </Box>
                    <TextInput
                      size="xs"
                      value={c.choice_label}
                      onChange={(e) => updateChoice(index, cIdx, 'choice_label', e.currentTarget.value.toUpperCase().slice(0, 2))}
                      styles={{ input: { width: 36, textAlign: 'center', fontWeight: 700, padding: '0 4px' } }}
                      mt={4}
                    />
                    <Box style={{ flex: 1 }}>
                      <TextInput
                        placeholder={`Choice ${c.choice_label} text`}
                        size="sm"
                        value={c.choice_text}
                        onChange={(e) => updateChoice(index, cIdx, 'choice_text', e.currentTarget.value)}
                      />
                      <TextInput
                        placeholder="Explanation (optional)"
                        size="xs"
                        mt={4}
                        value={c.explanation}
                        onChange={(e) => updateChoice(index, cIdx, 'explanation', e.currentTarget.value)}
                        styles={{ input: { fontSize: 12, color: 'var(--sat-text-soft)' } }}
                      />
                    </Box>
                    {qa.choices.length > 2 && (
                      <ActionIcon size="sm" variant="subtle" color="red" mt={4} onClick={() => removeChoice(index, cIdx)}>
                        <X size={12} />
                      </ActionIcon>
                    )}
                  </Group>
                </Paper>
              ))}
            </Stack>
            {qa.choices.length < 6 && (
              <Button
                leftSection={<Plus size={12} />}
                variant="subtle"
                color="gray"
                size="xs"
                onClick={() => addChoice(index)}
              >
                Add Choice
              </Button>
            )}
          </>
        )}

        <Textarea
          label="Explanation"
          placeholder="Why is the correct answer correct?"
          rows={2}
          value={qa.explanation || ''}
          onChange={(e) => updateQA(index, 'explanation', e.currentTarget.value)}
        />
      </Stack>
    </Paper>
  )
}
